import React, { memo, MouseEvent, ReactElement, useCallback, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useKey } from 'react-use'

import { PORTAL_CLASS_NAME } from '../../../../constants/portalClassName'
import { Icon } from '../../../Icon'
import { AspectRatio, FileType, PreviewFile } from '../../types'
import { FilesPreviewContent } from '../FilesPreviewContent'
import * as Styled from './styles'

interface FullScreenPreviewProps {
  aspectRatio?: AspectRatio
  asType?: FileType
  className: string
  files: PreviewFile[]
  onClose?: () => void
  visible?: boolean
  withDownloadButton?: boolean
  withOverlay?: boolean
  withRotate?: boolean
}

export const FullScreenPreview = memo(
  ({
    aspectRatio,
    asType,
    className,
    files,
    onClose,
    visible,
    withDownloadButton = true,
    withOverlay = false,
    withRotate = true,
  }: FullScreenPreviewProps): ReactElement => {
    const contentRef = useRef<HTMLDivElement>(null)

    const handleEscPress = useCallback(() => {
      if (visible) {
        onClose && onClose()
      }
    }, [onClose, visible])

    const handleBackgroundClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        const clickedElement = event.target as HTMLElement

        if (
          contentRef.current &&
          contentRef.current !== clickedElement &&
          !contentRef.current.contains(clickedElement)
        ) {
          onClose && onClose()
        }
      },
      [contentRef, onClose],
    )

    useKey('Escape', handleEscPress, undefined, [visible])

    return ReactDOM.createPortal(
      <Styled.FullScreenWrapper
        className={PORTAL_CLASS_NAME}
        onClick={handleBackgroundClick}
        visible={visible}
        withOverlay={withOverlay}
      >
        <Styled.ModalWrapper visible={visible} ref={contentRef}>
          <Styled.CloseWrapper onClick={onClose}>
            <Icon icon="x" size="lg" />
          </Styled.CloseWrapper>

          <Styled.FilesPreviewContentContainer aspectRatio={aspectRatio}>
            <FilesPreviewContent
              aspectRatio={aspectRatio}
              asType={asType}
              className={className}
              files={files}
              fitToHeight
              loading=""
              width="100%"
              withDownloadButton={withDownloadButton}
              withRotate={withRotate}
            />
          </Styled.FilesPreviewContentContainer>
        </Styled.ModalWrapper>
      </Styled.FullScreenWrapper>,
      document.body,
    )
  },
)
